import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import useSWR from 'swr';
import CrossIcon from '@b2b-frontend/components/icons/CrossIcon';
import Link from '@b2b-frontend/components/Link';
import Button from '@b2b-frontend/components/Button';
import SkeletonRow from '@b2b-frontend/components/SkeletonRow';
import FormError from '@b2b-frontend/components/FormError';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { createHeaderMenuClickEvent } from '@b2b-frontend/utils/analytics/gtm';
import { ROUTES } from '../../constants';
import { useBrands } from 'utils/useBrands';

interface IDropdownNavProps {
	onCloseRequested: () => void;
	isOpen: boolean;
}

export const DropdownNav: React.FC<IDropdownNavProps> = ({ onCloseRequested, isOpen }) => {
	const rootRef = useRef<HTMLDivElement>();
	const { data: brands = [], error, isValidating, mutate } = useBrands();

	const sendDataToGTM = useGTMDispatch();

	const handleClick = useCallback(
		event => {
			if (event.target.tagName === 'A') {
				onCloseRequested();
			}

			if (!rootRef.current) {
				return;
			}

			if (!rootRef.current.contains(event.target)) {
				onCloseRequested();
			}
		},
		[onCloseRequested],
	);

	useEffect(() => {
		if (isOpen) {
			rootRef.current?.querySelector('a')?.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		window.addEventListener('click', handleClick);

		return () => {
			window.removeEventListener('click', handleClick);
		};
	}, [handleClick]);

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (
			['ArrowUp', 'ArrowDown', 'Escape', 'Home', 'End', ' ', 'PageDown', 'PageUp'].includes(
				event.key,
			)
		) {
			event.preventDefault();
		}
	};

	const handleKeyUp = (event: React.KeyboardEvent) => {
		const key = event.key;

		switch (key) {
			case 'Escape':
				event.preventDefault();
				onCloseRequested();
				break;
			case 'ArrowUp':
				event.preventDefault();
				const previousElement =
					document.activeElement?.parentElement?.previousElementSibling;

				if (previousElement) {
					previousElement.querySelector('a')?.focus();
				}

				break;
			case 'ArrowDown':
				event.preventDefault();
				const nextElement = document.activeElement?.parentElement?.nextElementSibling;

				if (nextElement) {
					nextElement.querySelector('a')?.focus();
				}

				break;
			case 'PageUp':
			case 'Home':
				const firstLink = rootRef.current?.querySelector<HTMLAnchorElement>('li a');

				if (firstLink) {
					firstLink.focus();
				}

				break;
			case 'PageDown':
			case 'End':
				const lastLink =
					rootRef.current?.querySelector<HTMLAnchorElement>('li:last-of-type a');

				if (lastLink) {
					lastLink.focus();
				}

				break;
		}
	};

	const revalidate = async () => {
		await mutate();
	};

	return (
		<div
			// @ts-ignore
			ref={rootRef}
			onKeyUp={handleKeyUp}
			onKeyDown={handleKeyDown}
			className={clsx(
				'px-[60px] py-[40px] lg:px-[138px] lg:py-[70px]',
				'font-sans font-bold text-white uppercase text-xs bg-black bg-opacity-90',
			)}
		>
			<button
				onClick={onCloseRequested}
				className="absolute top-6 right-6 text-white w-10 h-10 inline-flex items-center justify-center"
			>
				<CrossIcon />
			</button>
			{!isValidating && error && (
				<FormError>
					<p>Something went wrong while fetching user brands: {error.message}</p>
					<Button onClick={revalidate}>Retry</Button>
				</FormError>
			)}
			<ul
				id="brand_popup"
				role="menu"
				aria-labelledby="brand_popover_control"
				className={clsx(
					'grid grid-cols-2 gap-x-16 gap-y-8 lg:gap-x-36 lg:gap-y-10',
					'md:grid-cols-3',
					'xl:grid-cols-nav',
				)}
			>
				{brands.length === 0 && isValidating && (
					<>
						<li>
							<SkeletonRow width={100} />
						</li>
						<li>
							<SkeletonRow width={110} />
						</li>
						<li>
							<SkeletonRow width={90} />
						</li>
						<li>
							<SkeletonRow width={140} />
						</li>
						<li>
							<SkeletonRow width={100} />
						</li>
						<li>
							<SkeletonRow width={65} />
						</li>
					</>
				)}
				{brands.map(brand => {
					return (
						<li role="none" key={brand.brand_slug}>
							<Link
								linkProps={{
									role: 'menuitem',
									onClick: () => {
										sendDataToGTM(
											createHeaderMenuClickEvent({
												clickArea: 'browse by brand',
												label: brand.brand,
											}),
										);
									},
								}}
								href={ROUTES.BRANDS.BRAND.ROOT.replace(
									'[brandName]',
									brand.brand_slug,
								)}
								className="hover:text-brand"
							>
								{brand.brand}
							</Link>
						</li>
					);
				})}
			</ul>
			<div className="flex items-center pt-[60px] justify-center">
				<Button
					href="/brands"
					className="transition-all bg-brand border-brand w-[185px] hover:bg-transparent hover:border-brand hover:text-brand focus:bg-transparent focus:border-brand focus:text-brand"
				>
					View all
				</Button>
			</div>
		</div>
	);
};

export default DropdownNav;
